import React, { useState } from 'react';
import { FaLock, FaGlobe, FaTimes, FaEye, FaEyeSlash } from 'react-icons/fa';

function AccessibilityModal({ idea, components, onClose, onUpdateComponent }) {
  const [componentVisibility, setComponentVisibility] = useState(
    components.reduce((acc, comp) => {
      acc[comp.id] = comp.is_public || false;
      return acc;
    }, {})
  );

  const handleToggleComponentVisibility = async (componentId) => {
    try {
      const token = localStorage.getItem('access_token');
      if (!token) return;

      const component = components.find(comp => comp.id === componentId);
      const newVisibility = !componentVisibility[componentId];
      
      // Update the component
      const updatedComponent = {
        ...component,
        is_public: newVisibility
      };
      
      await onUpdateComponent(componentId, updatedComponent);
      
      setComponentVisibility(prev => ({
        ...prev,
        [componentId]: newVisibility
      }));
    } catch (err) {
      console.error('Failed to update component visibility:', err);
    }
  };

  const publicComponents = components.filter(comp => componentVisibility[comp.id]);
  const privateComponents = components.filter(comp => !componentVisibility[comp.id]);

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4">
      <div className="bg-[var(--bg-primary)] rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <div className="p-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-[var(--primary)]/10 rounded-full flex items-center justify-center">
                <FaEye className="text-[var(--primary)] text-xl" />
              </div>
              <div>
                <h3 className="text-lg font-semibold text-[var(--text-primary)]">Component Accessibility</h3>
                <p className="text-sm text-[var(--text-secondary)]">Manage visibility for "{idea?.title}"</p>
              </div>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-[var(--text-secondary)] hover:text-[var(--text-primary)] transition-colors"
            >
              <FaTimes />
            </button>
          </div>

          {/* Overview Stats */}
          <div className="grid grid-cols-3 gap-4 mb-6">
            <div className="bg-[var(--bg-secondary)] p-4 rounded-lg text-center border border-[var(--border-color)]">
              <div className="text-2xl font-bold text-[var(--text-primary)]">{components.length}</div>
              <div className="text-sm text-[var(--text-secondary)]">Total Components</div>
            </div>
            <div className="bg-[var(--primary)]/10 border border-[var(--primary)]/20 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-[var(--primary)]">{publicComponents.length}</div>
              <div className="text-sm text-[var(--text-secondary)]">Public</div>
            </div>
            <div className="bg-[var(--secondary)]/10 border border-[var(--secondary)]/20 p-4 rounded-lg text-center">
              <div className="text-2xl font-bold text-[var(--secondary)]">{privateComponents.length}</div>
              <div className="text-sm text-[var(--text-secondary)]">Private</div>
            </div>
          </div>

          {/* Components List */}
          <div className="space-y-4">
            <h4 className="font-medium text-[var(--text-primary)] flex items-center gap-2">
              <FaEye className="text-[var(--primary)]" />
              Component Visibility
            </h4>
            
            {components.length === 0 ? (
              <div className="text-center py-8 text-[var(--text-secondary)]">
                <FaEyeSlash className="mx-auto text-4xl mb-3 opacity-50" />
                <p>No components found in this idea.</p>
                <p className="text-sm mt-1">Add some components to manage their visibility.</p>
              </div>
            ) : (
              <div className="space-y-2 max-h-96 overflow-y-auto">
                {components.map((component) => (
                  <div
                    key={component.id}
                    className="flex items-center justify-between p-4 bg-[var(--bg-secondary)] rounded-lg border border-[var(--border-color)] hover:border-[var(--primary)]/30 transition-colors"
                  >
                    <div className="flex items-center gap-3 flex-1 min-w-0">
                      <div className={`p-2 rounded-lg ${
                        componentVisibility[component.id] 
                          ? 'bg-[var(--primary)]/10 text-[var(--primary)]' 
                          : 'bg-[var(--secondary)]/10 text-[var(--secondary)]'
                      }`}>
                        {componentVisibility[component.id] ? <FaGlobe /> : <FaLock />}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h5 className="font-medium text-[var(--text-primary)] truncate">
                          {component.title}
                        </h5>
                        <p className="text-sm text-[var(--text-secondary)] capitalize">
                          {component.type.replace('_', ' ')} Component
                        </p>
                      </div>
                      <div className="text-right">
                        <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium ${
                          componentVisibility[component.id]
                            ? 'bg-[var(--primary)]/10 text-[var(--primary)]'
                            : 'bg-[var(--secondary)]/10 text-[var(--secondary)]'
                        }`}>
                          {componentVisibility[component.id] ? 'Public' : 'Private'}
                        </span>
                      </div>
                    </div>
                    <div className="ml-4">
                      <button
                        onClick={() => handleToggleComponentVisibility(component.id)}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full border border-[var(--text-primary)] transition-colors focus:outline-none focus:ring-2 focus:ring-[var(--primary)] focus:ring-offset-2 ${
                          componentVisibility[component.id] ? 'bg-[var(--primary)]' : 'bg-[var(--bg-primary)]'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-[var(--text-primary)] transition-transform ${
                            componentVisibility[component.id] ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Information Box */}
          <div className="mt-6 p-4 bg-[var(--primary)]/10 border border-[var(--primary)]/20 rounded-lg">
            <h5 className="font-medium text-[var(--primary)] mb-2">About Component Visibility</h5>
            <ul className="text-sm text-[var(--text-secondary)] space-y-1">
              <li>• <strong>Public components</strong> are visible to anyone with access to your idea</li>
              <li>• <strong>Private components</strong> are only visible to you and collaborators</li>
              <li>• Component visibility is independent of idea visibility</li>
              <li>• You can change visibility at any time</li>
            </ul>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-6">
            <button
              onClick={onClose}
              className="px-4 py-2 rounded-lg bg-[var(--primary)] text-white hover:opacity-90 transition-opacity"
            >
              Done
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

export default AccessibilityModal;
